import { useEffect } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router-dom'

import { TOKENS } from '../ui/dashboard-primitives'
import { chunkReloadAllowed, claimChunkReload, isChunkLoadError } from './chunkReload'
import { ActionAnchor, ActionButton } from './ErrorActions'
import { ErrorDetail } from './ErrorDetail'
import { ErrorState } from './ErrorState'
import { showErrorDetail } from './devMode'

const FULL_HEIGHT = 'min-h-[100dvh]'

/**
 * The `errorElement` for every route.
 *
 * WHY THIS EXISTS AT ALL. Before it, React Router had no error element
 * anywhere, so it fell back to its own default page ("Hey developer") for
 * every route failure, and `ErrorBoundary` in `main.tsx` never saw one: it sits
 * OUTSIDE `RouterProvider`, and the router catches its own errors first. The
 * boundary was not broken, it was simply never on the path.
 *
 * IT REPLACES THE SHELL RATHER THAN RENDERING INSIDE IT. Attached to the `/`
 * route, not to each child, so `Layout` itself throwing is covered too: a
 * boundary nested inside the thing that failed cannot be trusted to render.
 * The cost is that the rail is gone, which is why the primary action is a
 * document load to `/` rather than a client-side navigation. It is also why
 * this screen is full height while the 404 is not.
 *
 * A CHUNK ERROR IS NOT A GENERIC ERROR. `chunkReload.ts` carries the reasoning;
 * the short version is that a stale `index.html` is the single most likely way
 * a real user reaches this screen, and it is the one cause a reload actually
 * fixes. Recovering silently is better than explaining it.
 */
export function RouteError() {
  const error = useRouteError()

  const chunk = isChunkLoadError(error)

  // A PURE READ IN RENDER, THE CLAIM AND THE NAVIGATION IN THE EFFECT.
  // `chunkReloadAllowed` only reads storage, so the render pass stays
  // repeatable and the first paint already knows whether to show the quiet
  // recovery notice or the real error screen. Two earlier shapes were wrong and
  // the linter was right about both: deciding in the effect meant writing the
  // answer back with `setState` (cascading renders), and caching it in a ref
  // meant reading a ref during render.
  const wantsReload = chunk && chunkReloadAllowed()

  useEffect(() => {
    // Always log, whatever we show. A silent auto-reload must still leave a
    // trace in the console for whoever is debugging the deploy.
    console.error('[RouteError]', error)
  }, [error])

  useEffect(() => {
    if (!wantsReload) return
    // Claim and navigate. A refused claim means a StrictMode twin already
    // claimed and the page is on its way out, so there is nothing to do.
    //
    // ACCEPTED EDGE: storage that reads but does not write (quota exhausted, a
    // half-disabled private mode) refuses the claim with no reload in flight,
    // leaving this notice on screen until the user refreshes by hand. It cannot
    // loop, and the alternative -- reloading without recording it -- is exactly
    // the loop this module exists to prevent.
    if (claimChunkReload()) window.location.reload()
  }, [wantsReload])

  if (wantsReload) {
    return (
      <div className={`flex ${FULL_HEIGHT} w-full items-center justify-center px-6`}>
        <p
          className="text-[10px] uppercase tracking-[0.26em]"
          style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
        >
          Loading the latest version
        </p>
      </div>
    )
  }

  const detail = showErrorDetail() ? <ErrorDetail error={error} /> : undefined

  // A 404 thrown as a route response, rather than reached through the shell's
  // catch-all. Rare, but it should not read as a crash.
  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <ErrorState
          eyebrow="404"
        title="Page not found"
        body="We could not find that page. It may have moved, or the address has a typo."
        className={FULL_HEIGHT}
        actions={<ActionAnchor href="/">Back to home</ActionAnchor>}
        detail={detail}
      />
    )
  }

  return (
    <ErrorState
      eyebrow="Error"
      title="Something went wrong"
      body={
        chunk
          ? 'Part of the app could not be downloaded. Check your connection, then try again.'
          : 'This page could not be loaded. Your training data is safe.'
      }
      className={FULL_HEIGHT}
      actions={
        <>
          <ActionAnchor href="/">Back to home</ActionAnchor>
          <ActionButton onClick={() => window.location.reload()}>Try again</ActionButton>
        </>
      }
      detail={detail}
    />
  )
}
